'use client'

import { memo, useCallback } from 'react'
import { NinxlHeroDemoPlayer } from '@/app/ninxl/_components/ninxl-hero-demo-player'
import { useLandingHeroDemoSrc } from '@/lib/cms/use-landing-hero-demo-src'
import { CRAFT_MEDIA } from './craft-media'
import { CraftHeroAdam } from './craft-hero-adam'

/**
 * baby: Brand-first craft hero + Creation-of-Adam scroll beat.
 * Higgsfield loops under /ninxl/craft/vid/; Adam cutouts under /ninxl/craft/adam/.
 */
export const CraftHero = memo(function CraftHero() {
  // baby: Hero Watch demo = fixed CMS hero video (not the demos carousel).
  const demoSrc = useLandingHeroDemoSrc('ninxl')

  const scrollTo = useCallback((id: string) => {
    const target =
      document.getElementById(id) ??
      document.querySelector<HTMLElement>(`[data-section="${id}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <header className="craft-hero">
      <div className="craft-hero__grid">
        <div className="craft-reveal">
          <h1 className="craft-hero__brand">NINX</h1>
          <p className="craft-hero__headline">Private AI that lives on your desk.</p>
          <p className="craft-hero__support">
            Local models, system-wide voice, explainer workflows, screen reply, and coding agents.
            without shipping every draft to the cloud.
          </p>

          <div className="craft-hero__actions">
            <button
              type="button"
              className="craft-btn craft-btn--primary"
              onClick={() => scrollTo('download')}
            >
              Download
            </button>
            <button
              type="button"
              className="craft-btn craft-btn--ghost"
              onClick={() => scrollTo('features')}
            >
              Capabilities
            </button>
          </div>

          <div className="craft-hero__meta" aria-label="Highlights">
            <span className="craft-chip">Mac + Windows</span>
            <span className="craft-chip">On-device by default</span>
            <span className="craft-chip">InkFlow voice</span>
          </div>
        </div>

        <div className="craft-hero__visual craft-reveal craft-reveal--d2">
          <div className="craft-hero__frame">
            <span className="craft-tape craft-tape--tl" aria-hidden />
            <span className="craft-tape craft-tape--tr" aria-hidden />
            {/* baby: Craft hero loop; still image is the poster / fallback. */}
            <video
              className="h-auto w-full"
              src={CRAFT_MEDIA.heroVideo}
              poster={CRAFT_MEDIA.heroPoster}
              muted
              playsInline
              autoPlay
              loop
              preload="metadata"
              aria-label="NINX desktop preview"
            />
            {/* baby: CMS platform Watch demo for /ninxl: opens in dialog. */}
            <NinxlHeroDemoPlayer
              className="craft-hero__play"
              demoSrc={demoSrc}
              ariaLabel="Watch NINX demo"
              dialogTitle="Watch NINX demo"
              triggerLabel="Watch demo"
            />
          </div>
          {/* baby: Soft origami accent loop: decorative only. */}
          <video
            className="craft-hero__accent"
            src={CRAFT_MEDIA.origamiVideo}
            poster={CRAFT_MEDIA.origamiPoster}
            muted
            playsInline
            autoPlay
            loop
            preload="metadata"
            aria-hidden
          />
        </div>
      </div>

      {/* baby: Scroll past the hero intro: hands reach the folder. */}
      <CraftHeroAdam />
    </header>
  )
})

CraftHero.displayName = 'CraftHero'
